import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet.markercluster';
import { useLeafletContext } from '@react-leaflet/core';
import { renderToString } from 'react-dom/server';

const mcg = L.markerClusterGroup({
  spiderfyDistanceMultiplier: 2,
  iconCreateFunction: function (cluster) {
    // Convert the price string into number and sort.
    const sortedPrices = cluster.getAllChildMarkers().sort((a, b) => {
      const aPrice = parseFloat(a._tooltip._content.replace('€', ''));
      const bPrice = parseFloat(b._tooltip._content.replace('€', ''));

      if (aPrice > bPrice) {
        return 1;
      } else {
        return -1;
      }
    });

    return L.divIcon({
      html: `<div>${sortedPrices[0]._tooltip._content} - ${
        sortedPrices[sortedPrices.length - 1]._tooltip._content
      }</div>`,
      iconSize: [75, 50],
      className: 'marker-cluster',
    });
  },
});

/**
 * This element defines the marker clusters,
 * or the multiple nearby markers that are shown
 * with location count on the map, depending
 * on the zoom level.
 */
const MarkerCluster = ({ markers }) => {
  const { map } = useLeafletContext();

  useEffect(() => {
    mcg.clearLayers();
    markers.forEach(({ position, price, content }) => {
      // Define content for the opened popup.
      const popupHtml = (
        <div
          key={position.latitude + position.longitude}
          className="accommodation-search--leaflet"
        >
          {content}
        </div>
      );

      // Define the marker shown on the map.
      const marker = L.marker(
        new L.LatLng(position.latitude, position.longitude),
        {
          icon: L.divIcon({
            className: 'leaflet-mouse-marker',
            iconAnchor: [35, 20],
            iconSize: [70, 40],
          }),
        }
      );

      marker.bindPopup(renderToString(popupHtml), {
        maxWidth: '350px',
        offset: [0, -5],
        autoPan: false,
      });

      // If user hovers over the marker, show popup as long as
      // the cursor is over the marker, and don't pan to the popup.
      // If the user clicks the marker, keep it open and pan to it.
      marker.on('click', function (e) {
        // Pan to the clicked marker.
        const px = map.project(e.target._latlng);
        px.y -= e.target._popup._container.clientHeight / 2;
        map.panTo(map.unproject(px), { animate: true });
        marker.openPopup();
        marker.clicked = true;
        marker.hovered = false;
      });

      marker.on('popupclose', function () {
        if (marker.clicked) {
          marker.clicked = false;
        }
      });

      marker.on('mouseover', function () {
        // Open popup unless it was already opened by clicking the marker.
        if (!marker.clicked) {
          marker.openPopup();
          marker.hovered = true;
        }
      });

      marker.on('mouseout', function () {
        if (marker.hovered) {
          marker.hovered = false;
        }

        // If the popup wasn't opened by clicking,
        // close it when the user moves the cursor away from the marker.
        if (!marker.clicked) {
          marker.closePopup();
        }
      });

      // Text for the marker icon defined here.
      marker
        .bindTooltip(`${price} €`, {
          permanent: true,
          direction: 'center',
        })
        .addTo(mcg);

      return marker;
    });

    map.addLayer(mcg);
  }, [markers, map]);

  return null;
};

MarkerCluster.propTypes = {
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.objectOf(PropTypes.number).isRequired,
      price: PropTypes.string.isRequired,
      content: PropTypes.element,
    }).isRequired
  ).isRequired,
};

export default MarkerCluster;
